import React, {useState, useContext, useRef, useEffect, useMemo, lazy, Suspense, useCallback} from 'react';
import PropTypes from 'prop-types';
import {FmlxTooltip} from 'fmlx-common-ui';

import {Button} from 'react-bootstrap';
import TaskInfoWrapper from '../Components/TaskInfoWrapper';

import {
  Orientation,
  CardType,
  TaskType,
  WELL_HOVER_INDEX_COLOR_POSITION,
  ProtocolEditorViewMode,
} from '../../../constants/dataEnum';

import './DeckLabware.scss';

import {mergeRefs} from '../../../lib/refHelper';

import Spinner from '../../../components/Spinner/Spinner';

import {ProtocolEditorContext} from '../ContextProviders/ProtocolEditorContext';
import {LabwareZoomContext} from '../ContextProviders/LabwareZoomContext';
import useDeckZoom from './useDeckZoom';

import {checkContainIllegalChars} from '../../../lib/stringHelper';
import {filterToArray} from '../../../lib/objectHelper';

import {WELL_HOVER_MODE} from '../../../components/Labware/Labware';

function DeckLabwareFloatingButtons({onRemove, drag, hideDeleteButton, hideMoveButton, labwareRef}) {
  const [leftPosition, setLeftPosition] = useState(0);

  const getLeftPosition = useCallback(() => {
    const labwarePosition = labwareRef?.current.getBoundingClientRect();
    setLeftPosition(labwarePosition?.width);
  }, [labwareRef]);

  useEffect(() => {
    getLeftPosition();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    window.addEventListener('resize', getLeftPosition);

    return () => {
      window.removeEventListener('resize', getLeftPosition);
    };
    // eslint-disable-next-line
  }, [getLeftPosition]);

  return (
    <div className="deck-labware-floating-buttons" style={{left: leftPosition}}>
      {!hideDeleteButton && (
        <Button onClick={onRemove} disabled={hideDeleteButton}>
          <span className="glyphicon glyphicon-trash" />
        </Button>
      )}
      <div
        ref={(el) => {
          drag(el);
        }}
      >
        {!hideMoveButton && (
          <Button disabled={hideMoveButton}>
            <span className="glyphicon glyphicon-move" />
          </Button>
        )}
      </div>
    </div>
  );
}
DeckLabwareFloatingButtons.propTypes = {
  labwareRef: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  drag: PropTypes.func.isRequired,
  hideDeleteButton: PropTypes.bool,
  hideMoveButton: PropTypes.bool,
};

DeckLabwareFloatingButtons.defaultProps = {
  hideDeleteButton: false,
  hideMoveButton: false,
};

const DeckLabwareNameInput = React.memo(
  ({labwareName, onClick, onEditCommit, editMode, zoomMode, maxCharacters, showLabwareButton, barcode, style}) => {
    const [tempLabwareName, setTempLabwareName] = useState(labwareName || '');

    useEffect(() => {
      setTempLabwareName(labwareName);
    }, [labwareName]);

    function onCommitValue() {
      onEditCommit(tempLabwareName.trim());
      if (tempLabwareName.trim() === '') setTempLabwareName(labwareName);
    }

    const [helperWidth, setHelperWidth] = useState();
    const helperRef = useRef();
    const [labwareNameWidth, setLabwareNameWidth] = useState();
    const labwareNameRef = useRef();

    useEffect(() => {
      if (labwareNameRef.current) {
        setLabwareNameWidth(labwareNameRef.current.offsetWidth);
      }
      if (helperRef.current) {
        setHelperWidth(helperRef.current.offsetWidth);
      }
    }, [labwareName]);

    return (
      <>
        {!editMode && (
          <span
            ref={labwareNameRef}
            className={`deck-labware-name ${showLabwareButton ? 'show-button' : ''}`}
            onClick={onClick}
            style={style}
          >
            <FmlxTooltip title={helperWidth > labwareNameWidth ? tempLabwareName : ''}>
              <span>{tempLabwareName}</span>
            </FmlxTooltip>
            <span ref={helperRef} className="deck-labware-name-helper">
              {tempLabwareName}
            </span>
            {barcode && (
              <span
                className={`deck-labware-barcode ${showLabwareButton ? 'show-button' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="icon-barcode" />
                &nbsp;
                <FmlxTooltip title={helperWidth > labwareNameWidth ? barcode : ''}>
                  <span>{barcode}</span>
                </FmlxTooltip>
              </span>
            )}
          </span>
        )}
        {editMode && (
          <div className={`labware-name-edit-container ${zoomMode && 'zoom'}`}>
            <input
              type="text"
              className="labware-name-input"
              value={tempLabwareName}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onBlur={onCommitValue}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  onCommitValue();
                }
              }}
              onChange={({target}) => {
                if (maxCharacters - target.value.length >= 0 && !checkContainIllegalChars(target.value)) {
                  setTempLabwareName(target.value);
                }
              }}
            />
            <div className="index-remaining-char">{`(${maxCharacters - tempLabwareName.length}/${maxCharacters})`}</div>
          </div>
        )}
      </>
    );
  },
  (prev, next) =>
    prev.labwareName === next.labwareName &&
    prev.editMode === next.editMode &&
    prev.zoomMode === next.zoomMode &&
    prev.maxCharacters === next.maxCharacters &&
    prev.barcode === next.barcode &&
    prev.showLabwareButton === next.showLabwareButton,
);

DeckLabwareNameInput.propTypes = {
  labwareName: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  onEditCommit: PropTypes.func.isRequired,
  zoomMode: PropTypes.bool.isRequired,
  maxCharacters: PropTypes.number,
  showLabwareButton: PropTypes.bool,
  barcode: PropTypes.string,
  style: PropTypes.object,
};

DeckLabwareNameInput.defaultProps = {
  maxCharacters: 25,
  barcode: '',
  showLabwareButton: false,
  style: {},
};

const DeckLabware = React.forwardRef((props, ref) => {
  const {
    labware,
    drop,
    drag,
    dragPreview,
    itemID,
    onClickWell,
    hoverable = true,
    swipeEnable,
    onSwipeStart,
    onSwipeEnd,
    onSwipeMove,
    tasks,
    sdRawTasks,
    cardType,
    header,
    zoomable,
    selectable = true,
    disallowZoomOut,
    onZoomIn,
    onZoomOut,
    onRemoveLabware,
    runtimeMode,
    onLabwareClick,
    selectedWellStyle,
    renameLabware,
    taskWellColor,
    onHoverWell,
    onMouseLeave,
    onLoaded,
    normalizationTasks,
    isHoveredLabware,
    isOtherLabwareHovered,
    initHoveredWell,
    maxCharacters,
    showLabwareName,
    labwareName,
    barcode,
    oneDimensionalSwipeOnly,
    isDeepWell,
    isSelected,
    layouts,
    showTS,
    onHoverTransformSelection,
    onDragTransformSelection,
    onDropTransformSelection,
    protocolEditorMode,
    showLabwareButton,
    readOnly,
    labwareSelectedForNormalization,
  } = props;

  const {volumeDetailViewChecked, selectedCardRef} = useContext(ProtocolEditorContext) || {};

  const {setIsLabwareZoom, isLabwareZoom} = useContext(LabwareZoomContext) || {};

  const {LabwareID, colCount, rowCount, isWellRectangle, isIFC, labwareIndexes, isSpearBio} = labware;
  const is384 = rowCount === 16 && colCount === 24;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Labware = useMemo(
    () => lazy(() => import('../../../components/Labware')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [LabwareID, oneDimensionalSwipeOnly],
  );

  const [isEditingName, setIsEditingName] = useState(false);
  const [isMouseOverLabware, setIsMouseOverLabware] = useState(false);

  const containerRef = useRef(null);

  const allowZoomIn = zoomable && !isEditingName;
  const allowZoomOut = !disallowZoomOut && !isEditingName;
  const zoom = useDeckZoom(containerRef, itemID, allowZoomIn, allowZoomOut, onZoomIn, onZoomOut);

  useEffect(() => {
    // isLabwareZoom value is changed when user is dragging on broadcast/pooling tasks
    // so that the sticky arrow position would be rerendered and updated.

    if (isHoveredLabware && !runtimeMode) {
      setIsLabwareZoom(!zoom);
    } else if (!runtimeMode) {
      setIsLabwareZoom(zoom);
    }
  }, [zoom, setIsLabwareZoom, runtimeMode, isHoveredLabware]);

  const hoverableRef = useRef(hoverable);
  useEffect(() => {
    hoverableRef.current = hoverable;
  }, [hoverable]);

  const swipeEnableRef = useRef(swipeEnable);
  useEffect(() => {
    swipeEnableRef.current = swipeEnable;
  }, [swipeEnable]);

  // #region labware well selection action
  const handleHoverWell = (wellID, isCtrl, isShift) => {
    if (!hoverableRef.current) return;
    const [row, column, index] = wellID.split('-');
    onHoverWell(itemID, Number(row), Number(column), Number(index), isCtrl, isShift);
  };

  function handleClickWell(wellID) {
    const [row, column, index] = wellID.split('-');
    onClickWell(itemID, Number(row), Number(column), Number(index));
  }

  function handleSwipeStart(wellID, isCtrl, isShift) {
    if (!swipeEnableRef.current) return;
    const [row, column, index] = wellID.split('-');
    onSwipeStart(itemID, Number(row), Number(column), Number(index), isCtrl, isShift);
  }

  function handleSwipeEnd(wellID, startWellId, isCtrl, isShift) {
    if (!swipeEnableRef.current) return;
    const [row, column, index] = wellID.split('-');
    const [startRow, startColumn] = startWellId?.split('-') || '';
    onSwipeEnd(
      itemID,
      Number(row),
      Number(column),
      Number(startRow),
      Number(startColumn),
      Number(index),
      isCtrl,
      isShift,
    );
  }

  function handleSwipeMove(wellID, swipingDirectionY, swipingDirectionX, isCtrl, isShift) {
    if (!swipeEnableRef.current) return;
    const [row, column, index] = wellID.split('-');
    onSwipeMove(
      itemID,
      Number(row),
      Number(column),
      Number(index),
      swipingDirectionY,
      swipingDirectionX,
      isCtrl,
      isShift,
    );
  }
  // #endregion labware well selection action

  const getTasks = () => {
    switch (cardType) {
      case CardType.SERIAL_DILUTION:
        return sdRawTasks;
      case CardType.NORMALIZATION:
        return normalizationTasks;
      case CardType.BROADCAST: {
        const broadcastTasks = filterToArray(tasks, {type: [TaskType.ASPIRATE, TaskType.DISPENSE]}).map((e) => ({
          ...e,
          number: e.type === TaskType.ASPIRATE ? 1 : 2,
        }));

        const dispenseTask = broadcastTasks.find((ele) => ele.type === TaskType.DISPENSE);

        if (dispenseTask) {
          broadcastTasks.push({
            number: 3,
            ID: 3,
            type: 'Dispense',
            labwareID: dispenseTask.labwareID,
          });
        }

        return broadcastTasks;
      }
      case CardType.POOLING: {
        const poolingTasks = filterToArray(tasks, {type: [TaskType.ASPIRATE, TaskType.DISPENSE]}).map((e) => ({
          ...e,
          number: e.type === TaskType.ASPIRATE ? 1 : 3,
        }));

        if (poolingTasks.length > 0 && poolingTasks[0].volume && poolingTasks[0].type !== TaskType.DISPENSE) {
          poolingTasks.push({
            ...poolingTasks[0],
            number: 2,
          });
        }

        return poolingTasks;
      }
      default:
        return tasks;
    }
  };

  const isLabwareZoomed = () => {
    if ((!zoom && isHoveredLabware) || (zoom && !isOtherLabwareHovered)) {
      return `zoom zoom-${itemID}`;
    }
    return 'false';
  };

  return (
    <div
      ref={mergeRefs(containerRef)}
      className={`deck-labware ${isLabwareZoomed()}
      ${selectable && isSelected && 'selected'}
        `}
    >
      <span className="deck-labware-header">{header}</span>
      <TaskInfoWrapper
        style={{height: '90%'}}
        tasks={getTasks()}
        showTaskVolume={cardType === CardType.NORMALIZATION ? false : volumeDetailViewChecked}
        showTaskNumber={
          !runtimeMode &&
          selectedCardRef.current?.type !== CardType.DELAY &&
          selectedCardRef.current?.type !== CardType.CONFIRMATION
        }
        cardType={cardType}
        key={`task-info-wrapper-${itemID}`}
      >
        <div
          className={`deck-labware-content ${labwareSelectedForNormalization ? 'labware-selected-for-normalization' : ''}`}
          key={`deck-labware-content-${itemID}`}
        >
          <div
            className="deck-labware-content-labware"
            ref={mergeRefs(ref, drop, dragPreview)}
            onClick={() => onLabwareClick(itemID)}
            onMouseOver={() => {
              if (!isMouseOverLabware) setIsMouseOverLabware(true);
            }}
            onMouseLeave={() => {
              setIsMouseOverLabware(false);
              onMouseLeave(itemID);
            }}
            role="presentation"
          >
            <Suspense key={`${LabwareID}-${itemID}-container`} fallback={<Spinner />}>
              <Labware
                key={`${LabwareID}-${itemID}`}
                ID={itemID}
                colCount={isIFC ? 1 : colCount}
                rowCount={isIFC ? 1 : rowCount}
                orientation={Orientation.POTRAIT}
                isWellRectangle={isWellRectangle}
                onHoverWell={handleHoverWell}
                onClickWell={handleClickWell}
                wellStyle={selectedWellStyle ? {...taskWellColor, ...selectedWellStyle} : taskWellColor}
                showColIdx={isIFC ? false : [1]}
                showRowIdx={isIFC ? false : ['A']}
                swipeEnable={swipeEnable}
                oneDimensionalSwipeOnly={oneDimensionalSwipeOnly}
                onSwipeStart={handleSwipeStart}
                onSwipeEnd={handleSwipeEnd}
                onSwipeMove={handleSwipeMove}
                onLoaded={onLoaded}
                onMouseLeave={onMouseLeave}
                showSelectionLine={false}
                zoom={zoom}
                isHoveredLabware={isHoveredLabware}
                isSelected={!!isSelected && isLabwareZoom}
                wellHoverMode={
                  // eslint-disable-next-line no-nested-ternary
                  runtimeMode || protocolEditorMode === ProtocolEditorViewMode.PICKLIST || isIFC || !zoom
                    ? WELL_HOVER_MODE.NOTHING
                    : is384
                      ? WELL_HOVER_MODE.INDEX_COLOR
                      : WELL_HOVER_MODE.BOTH
                }
                wellHoverHighlightPosition={WELL_HOVER_INDEX_COLOR_POSITION.BOTH}
                initHoveredWell={initHoveredWell}
                showLabwareName={showLabwareName}
                labwareName={labware?.originalName}
                isDeepWell={isDeepWell}
                showTS={showTS}
                layouts={layouts}
                onHoverTransformSelection={onHoverTransformSelection}
                onDragTransformSelection={onDragTransformSelection}
                onDropTransformSelection={onDropTransformSelection}
                labwareIndexes={labwareIndexes}
                isIFC={isIFC}
                IFCvariant={labware.colCount}
                isSpearBio={isSpearBio}
              />
            </Suspense>
          </div>
          {((selectable && isSelected) || (selectable && showLabwareButton)) && (
            <DeckLabwareFloatingButtons
              labwareRef={ref}
              drag={isMouseOverLabware ? () => {} : drag}
              onRemove={() => onRemoveLabware(itemID)}
              hideDeleteButton={readOnly}
              hideMoveButton={readOnly}
            />
          )}
        </div>
      </TaskInfoWrapper>
      <DeckLabwareNameInput
        labwareName={labwareName}
        editMode={isEditingName}
        onClick={() => {
          if (!runtimeMode && !readOnly) setIsEditingName(true);
        }}
        onEditCommit={(name) => {
          setIsEditingName(false);
          if (name) renameLabware(itemID, name);
        }}
        zoomMode={zoom}
        maxCharacters={maxCharacters}
        showLabwareButton={showLabwareButton}
        barcode={barcode}
      />
    </div>
  );
});

DeckLabware.propTypes = {
  labware: PropTypes.object.isRequired,
  drag: PropTypes.func,
  drop: PropTypes.any,
  dragPreview: PropTypes.func,
  onClickWell: PropTypes.func,
  itemID: PropTypes.number.isRequired,
  hoverable: PropTypes.bool,
  tasks: PropTypes.array,
  sdRawTasks: PropTypes.array,
  cardType: PropTypes.string,
  header: PropTypes.string,
  zoomable: PropTypes.bool,
  selectable: PropTypes.bool,
  disallowZoomOut: PropTypes.bool,
  onZoomIn: PropTypes.func,
  onZoomOut: PropTypes.func,
  onRemoveLabware: PropTypes.func,
  runtimeMode: PropTypes.bool,
  onLabwareClick: PropTypes.func,
  selectedWellStyle: PropTypes.object,
  renameLabware: PropTypes.func,
  taskWellColor: PropTypes.object,
  swipeEnable: PropTypes.bool,
  onSwipeStart: PropTypes.func,
  onSwipeEnd: PropTypes.func,
  onSwipeMove: PropTypes.func,
  onHoverWell: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onLoaded: PropTypes.func,
  normalizationTasks: PropTypes.array,
  isHoveredLabware: PropTypes.any,
  isOtherLabwareHovered: PropTypes.bool.isRequired,
  initHoveredWell: PropTypes.object,
  maxCharacters: PropTypes.number,
  showLabwareName: PropTypes.bool,
  labwareName: PropTypes.string,
  barcode: PropTypes.any,
  oneDimensionalSwipeOnly: PropTypes.bool,
  isDeepWell: PropTypes.bool,
  isSelected: PropTypes.bool,
  showTS: PropTypes.bool,
  layouts: PropTypes.object,
  onHoverTransformSelection: PropTypes.func,
  onDragTransformSelection: PropTypes.func,
  onDropTransformSelection: PropTypes.func,
  protocolEditorMode: PropTypes.string,
  showLabwareButton: PropTypes.bool,
  readOnly: PropTypes.bool.isRequired,
  labwareSelectedForNormalization: PropTypes.bool,
};

DeckLabware.defaultProps = {
  drag: () => {},
  drop: () => {},
  dragPreview: () => {},
  onClickWell: () => {},
  hoverable: true,
  tasks: [],
  sdRawTasks: [],
  cardType: '',
  header: '',
  zoomable: false,
  selectable: true,
  disallowZoomOut: false,
  onZoomIn: () => {},
  onZoomOut: () => {},
  onRemoveLabware: () => {},
  runtimeMode: false,
  onLabwareClick: () => {},
  selectedWellStyle: {},
  taskWellColor: {},
  swipeEnable: false,
  onSwipeStart: () => {},
  onSwipeEnd: () => {},
  onSwipeMove: () => {},
  onHoverWell: () => {},
  onMouseLeave: () => {},
  onLoaded: () => {},
  renameLabware: () => {},
  normalizationTasks: [],
  isHoveredLabware: null,
  initHoveredWell: {row: null, column: null},
  maxCharacters: 25,
  showLabwareName: false,
  labwareName: '',
  barcode: '',
  oneDimensionalSwipeOnly: true,
  isDeepWell: false,
  isSelected: false,
  showTS: false,
  layouts: {},
  onHoverTransformSelection: () => {},
  onDragTransformSelection: () => {},
  onDropTransformSelection: () => {},
  protocolEditorMode: '',
  showLabwareButton: false,
  labwareSelectedForNormalization: false,
};

export default DeckLabware;
